import { ShowTypes, isTvDevice } from "@ca.vmedia.tv/domains";
import { AppConstants } from "@/common";
import { TimeUtils, Logger } from "@/utils";
import { linkItems } from "@/components/Header/types/LinkItemsCollection";
import type { ExposeInterface } from './types';

export const useApplicationStore = defineStore('Application', (): ExposeInterface => {
    const { $domains } = useNuxtApp();

    const _basicRoute: Ref<string | null> = ref(null);
    const getBasicRoute: ComputedRef<string | null> = computed(() => _basicRoute.value);
    const setBasicRoute = (route: string | null): void  => {
        _basicRoute.value = route;
    }

    const isMyTvBasicRoute: ComputedRef<boolean> = computed(() => getBasicRoute.value === linkItems.myTv.routerPath);

    const _imageUrlTemplate: Ref<string> = ref("");
    /**
     * Set image url temaplate on application start
     * @param template The template string
     */
    const setImageUrlTemplate = (template: string): void  => {
        _imageUrlTemplate.value = template;
    };

    const _imageWorkaroundUrlTemplate: Ref<string> = ref("");
    /**
     * Set image workaround url temaplate on application start
     * @param template The template string
     */
    const setImageWorkaroundUrlTemplate = (template: string): void  => {
        _imageWorkaroundUrlTemplate.value = template;
    };
    /**
     * Get Image url templates action
     */
    const getImageUrlTemplates = async (): Promise<void>  => {
        try {
            const templates = await $domains.imagesRepository.getImageUrlTemplates();
            $domains.domainsSettings.imagesWorkaroundUrlTemplate = templates?.defaults ?? "";
            $domains.domainsSettings.imagesUrlTemplate = templates?.imageId ?? "";

            setImageWorkaroundUrlTemplate($domains.domainsSettings.imagesWorkaroundUrlTemplate);
            setImageUrlTemplate($domains.domainsSettings.imagesUrlTemplate);
        } catch (error) {
            Logger.captureError(error as Error);
        }
    };

    const _focusedElements: Ref<Set<string>> = ref(new Set());
    const getFocusedElements: ComputedRef<Set<string>> = computed(() => _focusedElements.value);
    /**
     * Add component to list of focused components
     * @param componentName Component name
     */
    const addFocusedElement = (componentName: string): void  => {
        _focusedElements.value.add(componentName);
    };
    /**
     * Remove component from list of focused components
     * @param componentName Component name
     */
    const removeFocusedElement = (componentName: string): void  => {
        _focusedElements.value.delete(componentName);
    };

    /**
     * The property is responsible for showing the modal window "sign out"
     */
    const _isModalSignOutShown: Ref<boolean> = ref(false);
    const getIsModalSignOutShown: ComputedRef<boolean> = computed(() => _isModalSignOutShown.value);
    /**
     * Open modal window 'sign out'
     */
    const openModalSignOut = (): void  => {
        _isModalSignOutShown.value = true;
    };
    /**
     * Close modal window 'sign out'
     */
    const closeModalSignOut = (): void  => {
        _isModalSignOutShown.value = false;
    }

    /**
     * The property is responsible for showing the modal window "exit app"
     */
    const _isModalExitAppShown: Ref<boolean> = ref(false);
    const getIsModalExitAppShown: ComputedRef<boolean> = computed(() => _isModalExitAppShown.value);
    /**
     * Open modal window 'exit app'
     */
    const openModalExitApp = (): void  => {
        _isModalExitAppShown.value = true;
    };
    /**
     * Close modal window 'exit app'
     */
    const closeModalExitApp = (): void  => {
        _isModalExitAppShown.value = false;
    };

    const _isModalExpiredCodeShown: Ref<boolean> = ref(false);
    const getIsModalExpiredCodeShown: ComputedRef<boolean> = computed(() => _isModalExpiredCodeShown.value);
    const openModalExpiredCode = (): void  => {
        _isModalExpiredCodeShown.value = true;
    };
    
    const closeExpiredCode = (): void  => {
        _isModalExpiredCodeShown.value = false;
    };

    const _isModalSignInUnavailableShown: Ref<boolean> = ref(false);
    const getIsModalSignInUnavailableShown: ComputedRef<boolean> = computed(() => _isModalSignInUnavailableShown.value);
    const openModalSignInUnavailable = (): void  => {
        _isModalSignInUnavailableShown.value = true;
    };
    
    const closeSignInUnavailable = (): void  => {
        _isModalSignInUnavailableShown.value = false;
    };

    /**
     * The property is responsible for the visibility of the header
     */
    const _isHeaderVisible: Ref<boolean> = ref(true);
    const getHeaderVisibility: ComputedRef<boolean> = computed(() => _isHeaderVisible.value);
    /**
     * Hide header
     */
    const hideHeader = (): void  => {
        if (isTvDevice){
            _isHeaderVisible.value = false;
        }
    };
    /**
     * Show header
     */
    const showHeader = (): void  => {
        if (isTvDevice){
            _isHeaderVisible.value = true;
        }
    };

    /**
     * ID of the selected program
     */
    const _selectedIdProgram: Ref<string[]> = ref([]);
    const getCurrentSelectedIdProgram: ComputedRef<string | null> = computed(() => {
        const id = _selectedIdProgram.value.slice(0).pop();
        return id ? String(id) : null;
    });
    /**
     * Delete all program ID
     */
    const deleteAllSelectedIdProgram = (): void  => {
        _selectedIdProgram.value = [];
    };
    /**
     * Set program ID when going to detail page
     * @param selectedIdProgram id of the selected program {[key: string]: string}
     */
    const setSelectedIdProgram = (selectedIdProgram: string[]): void  => {
        _selectedIdProgram.value = [...new Set(_selectedIdProgram.value.concat(selectedIdProgram))];
    };
    /**
     * Delete program ID
     */
    const deleteSelectedIdProgram = (): void  => {
        if (_selectedIdProgram.value) {
            if (_selectedIdProgram.value.length == 1) {
                deleteAllSelectedIdProgram();
            } else {
                const programId: string = _selectedIdProgram.value[_selectedIdProgram.value.length - 1];
                _selectedIdProgram.value = _selectedIdProgram.value.filter(element => element !== programId);
            }
        }
    };
    const addSelectedIdProgram = async (id: string | null): Promise<void>  => {
        const selectedIdProgram: string[] = [];
        if (id) {
            selectedIdProgram.push(id);
        }

        setSelectedIdProgram(selectedIdProgram);
    };

    const _isHavSelectedIdProgram: Ref<boolean> = ref(false);
    const getIsHaveFocusedElement: ComputedRef<boolean> = computed(() => _isHavSelectedIdProgram.value);
    const setIsHaveFocusedElement = (payload: boolean): void  => {
        _isHavSelectedIdProgram.value = payload;
    };

    const _isPreviewSingInModal: Ref<boolean> = ref(false);
    const getIsPreviewSingInModal: ComputedRef<boolean> = computed(() => _isPreviewSingInModal.value);
    const showPreviewSignInModal = (show: boolean): void  => {
        _isPreviewSingInModal.value = show;
    };

    /**
     * Forbids rerouting
     */
    const _disableRouteChange: Ref<boolean> = ref(false);
    const getDisableRouteChange: ComputedRef<boolean> = computed(() => _disableRouteChange.value);
    const setDisableRouteChange = (payload: boolean): void  => {
        _disableRouteChange.value = payload;
    };

    const _isInternetModalShow: Ref<boolean> = ref(false);
    const getIsInternetModalShow: ComputedRef<boolean> = computed(() => _isInternetModalShow.value);
    const showInternetModal = (show: boolean): void  => {
        _isInternetModalShow.value = show;
    };

    /**
     * displayed shelf counter
     */
    const _counterLinesSearch: Ref<number> = ref(AppConstants.SearchCounterLines);
    const getCounterSearch: ComputedRef<number> = computed(() => _counterLinesSearch.value);
    const setCounterSearch = (number: number): void  => {
        _counterLinesSearch.value = number;
    };

    const _tooltipeMessage: Ref<string> = ref('');
    const getTooltipeMessage: ComputedRef<string> = computed(() => _tooltipeMessage.value);
    const setTooltipeMessage = (message: string): void  => {
        _tooltipeMessage.value = message;
    };

    const _tooltipeShowingTime: Ref<number> = ref(0);
    const _tooltipeShowing: Ref<boolean> = ref(false);
    const getTooltipeShowing: ComputedRef<boolean> = computed(() => _tooltipeShowing.value);
    const getShowingTooltipeTime: ComputedRef<number> = computed(() => _tooltipeShowingTime.value);
    const setTooltipeShowing = (payload: {visible: boolean; showingTime: number}): void  => {
        _tooltipeShowing.value = payload.visible;
        _tooltipeShowingTime.value = payload.showingTime;
    };

    const _visibleForgot: Ref<boolean> = ref(false);
    const getVisibleForgot: ComputedRef<boolean> = computed(() => _visibleForgot.value);

    const _isDisabledSentry: Ref<boolean> = ref(false);
    const getIsDisabledSentry: ComputedRef<boolean> = computed(() => _isDisabledSentry.value);
    const setEnableSentry = (): void  => {
        _isDisabledSentry.value = false;
    };
    const setDisableSentry = (): void  => {
        _isDisabledSentry.value = true;
    };

    /**
     * current time ms format
     */
    const _currentTime: Ref<number> = ref(TimeUtils.dateNow());
    const getCurrentTime: ComputedRef<number> = computed(() => _currentTime.value);
    const setCurrentTime = (): void  => {
        _currentTime.value = TimeUtils.dateNow();
    };

    const _intevalClearingStorageMs: number = 1 * 60 * 60 * 1000; // 1 hours
    let _timeClearingStorageMs: number = TimeUtils.dateNow() + _intevalClearingStorageMs;
    const checkTimeClearingStorage = (): boolean => {
        if (_currentTime.value > _timeClearingStorageMs) {
            _timeClearingStorageMs = _currentTime.value + _intevalClearingStorageMs;
            return true;
        }
        return false;
    };

    const _isOktaTokenErrorModal: Ref<boolean> = ref(false);
    const getIsOktaTokenErrorModal: ComputedRef<boolean> = computed(() => _isOktaTokenErrorModal.value);
    const showOktaTokenErrorModal = (show: boolean): void  => {
        _isOktaTokenErrorModal.value = show;
    };

    const _isOktaServerErrorModal: Ref<boolean> = ref(false);
    const getIsOktaServerErrorModal: ComputedRef<boolean> = computed(() => _isOktaServerErrorModal.value);
    const showServerErrorModal = (show: boolean): void  => {
        _isOktaServerErrorModal.value = show;
    };

    /**
     * The property is responsible for showing the modal window "reload app"
     */
    const _isModalReloadAppShown: Ref<boolean> = ref(false);
    const getIsModalReloadAppShow: ComputedRef<boolean> = computed(() => _isModalReloadAppShown.value);
    /**
     * Open modal window 'reload app'
     */
    const openModalReloadApp = (): void  => {
        _isModalReloadAppShown.value = true;
    }
    /**
     * Close modal window 'reload app'
     */
    const closeModalReloadApp = (): void  => {
        _isModalReloadAppShown.value = false;
    };
    
    const _filterVod: Ref<string | null> = ref(null);
    const getFilterVod: ComputedRef<string | null> = computed(() => _filterVod.value);
    const getFilterVodParams = computed((): { showType: ShowTypes | undefined; cashDisabled: boolean } => {
        let showType = undefined;
        let cashDisabled= false;
        if (_filterVod.value) {
            switch (_filterVod.value) {
                case "All":
                    showType = ShowTypes.None;
                break;

                case "Series":
                    showType = ShowTypes.Series;
                break;

                case "Movie":
                    showType = ShowTypes.Movie;
                break;
            }
            cashDisabled = true;
        }
        return {showType, cashDisabled};
    });
    const setFilter = (value: string): void  => {
        _filterVod.value = value;
    };

    const _isLoadingPageIndicator: Ref<boolean> = ref(false);
    const getIsLoadingPageIndicator: ComputedRef<boolean> = computed(() => _isLoadingPageIndicator.value);
    const setIsLoadingPageIndicator= (state: boolean): void  => {
        _isLoadingPageIndicator.value = state;
    };

    const _isGlobalMaintenanceModalShow: Ref<boolean> = ref(false);
    const getIsGlobalMaintenanceModalShow: ComputedRef<boolean> = computed(() => _isGlobalMaintenanceModalShow.value);
    const showGlobalMaintenanceModal = (show: boolean): void  => {
        _isGlobalMaintenanceModalShow.value = show;
    };

    const _maintenanceTimerInSeconds: Ref<number> = ref(0);
    const getMaintenanceTimerInSeconds = computed((): number => _maintenanceTimerInSeconds.value);
    const setMaintenanceTimerInSeconds = (value: number): void  => {
        _maintenanceTimerInSeconds.value = value;
    };

    const _isPlayerMaintenanceModalShow: Ref<boolean> = ref(false);
    const getIsPlayerMaintenanceModalShow: ComputedRef<boolean> = computed(() => _isPlayerMaintenanceModalShow.value);
    const showPlayerMaintenanceModal = (show: boolean): void  => {
        _isPlayerMaintenanceModalShow.value = show;
    };

    const _isRequestIsLocked: Ref<boolean> = ref(false);
    const getIsRequestIsLocked = computed(() => _isRequestIsLocked.value);
    const setIsRequestIsLocked = (lock: boolean): void  => {
        _isRequestIsLocked.value = lock;
    };

    const _manualRetryLoading: Ref<boolean> = ref(false);
    const getManualRetryLoading = computed(() => _manualRetryLoading.value);
    const setManualRetryLoading = (lock: boolean): void  => {
        _manualRetryLoading.value = lock;
    };

    const _is507ErrorModalShow: Ref<boolean> = ref(false);
    const _modal507Message: Ref<string> = ref('');
    const getIs507ErrorModalShow: ComputedRef<boolean> = computed(() => _is507ErrorModalShow.value);
    const getModal507Message: ComputedRef<string> = computed(() => _modal507Message.value);
    const setIsShown507ErrorModal = (show: boolean): void  => {
        _is507ErrorModalShow.value = show;
    };
    const setModal507Message = (message: string): void  => {
        _modal507Message.value = message;
    };

    const _isEnabledDevMode: Ref<boolean> = ref(false);
    const getIsEnabledDevMode: ComputedRef<boolean> = computed(() => _isEnabledDevMode.value);
    const setIsEnabledDevMode = (state: boolean): void  => {
        _isEnabledDevMode.value = state;
    };

    const clearAllApplication = (): void  => {
        _focusedElements.value.clear();
        deleteAllSelectedIdProgram();
        _isHavSelectedIdProgram.value = false;
        _isPreviewSingInModal.value = false;
        _disableRouteChange.value = false;
        _isInternetModalShow.value = false;
        _counterLinesSearch.value = AppConstants.SearchCounterLines;
        _tooltipeShowing.value = false;
        _visibleForgot.value = false;
        _isDisabledSentry.value = false;
        _currentTime.value = TimeUtils.dateNow();
        _tooltipeMessage.value = '';
        _tooltipeShowingTime.value = 0;
        _filterVod.value = null;
        _isOktaTokenErrorModal.value = false;
        _isOktaServerErrorModal.value = false;
        _isModalReloadAppShown.value = false;
        _isGlobalMaintenanceModalShow.value = false;
        _isPlayerMaintenanceModalShow.value = false;
        _isRequestIsLocked.value = false;
        _manualRetryLoading.value = false;
        _maintenanceTimerInSeconds.value = 0;
        _is507ErrorModalShow.value = false;
        _modal507Message.value = '';
        _isEnabledDevMode.value = false;
    };


    if(window) {
        setCurrentTime();
        setInterval(() => {
            setCurrentTime();
        }, 60000);
    }
    
    return {
        getIsLoadingPageIndicator,
        getFilterVodParams,
        getFilterVod,
        getCurrentTime,
        getIsModalReloadAppShow,
        getShowingTooltipeTime,
        getIsDisabledSentry,
        getVisibleForgot,
        getTooltipeMessage,
        getTooltipeShowing,
        getCounterSearch,
        getIsInternetModalShow,
        getDisableRouteChange,
        getIsOktaServerErrorModal,
        getIsOktaTokenErrorModal,
        getIsPreviewSingInModal,
        getCurrentSelectedIdProgram,
        getIsModalSignInUnavailableShown,
        getIsModalExpiredCodeShown,
        getIsModalSignOutShown,
        getIsModalExitAppShown,
        getFocusedElements,
        getHeaderVisibility,
        getIsHaveFocusedElement,
        getIsGlobalMaintenanceModalShow,
        getMaintenanceTimerInSeconds,
        getIsPlayerMaintenanceModalShow,
        getIsRequestIsLocked,
        getManualRetryLoading,
        setIsLoadingPageIndicator,
        addSelectedIdProgram,
        getImageUrlTemplates,
        setIsHaveFocusedElement,
        hideHeader,
        showHeader,
        openModalExitApp,
        closeModalExitApp,
        addFocusedElement,
        removeFocusedElement,
        clearAllApplication,
        deleteSelectedIdProgram,
        deleteAllSelectedIdProgram,
        showPreviewSignInModal,
        showOktaTokenErrorModal,
        showServerErrorModal,
        setDisableRouteChange,
        showInternetModal,
        setCounterSearch,
        setTooltipeShowing,
        setTooltipeMessage,
        setEnableSentry,
        setDisableSentry,
        setCurrentTime,
        openModalSignOut,
        closeModalSignOut,
        setFilter,
        openModalExpiredCode,
        closeExpiredCode,
        closeSignInUnavailable,
        openModalSignInUnavailable,
        openModalReloadApp,
        closeModalReloadApp,
        showGlobalMaintenanceModal,
        setMaintenanceTimerInSeconds,
        showPlayerMaintenanceModal,
        setIsRequestIsLocked,
        setManualRetryLoading,
        getIs507ErrorModalShow,
        getModal507Message,
        setIsShown507ErrorModal,
        setModal507Message,
        setBasicRoute,
        getBasicRoute,
        isMyTvBasicRoute,
        getIsEnabledDevMode,
        setIsEnabledDevMode,
        checkTimeClearingStorage
    };
});